import { PlusOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Button,
  Table,
  Flex,
  FormInstance,
  Spin,
  Empty,
  InputNumber,
  Card
} from 'antd'
import { FormListFieldData, TableColumnsType } from 'antd/lib'
import { useTheme } from 'antd-style'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useStocksServiceGetInventoryListEndpointKey } from 'src/api/queries'
import {
  InternalInventoryDocumentResponse,
  InventoryListResponse,
  IssuanceAllowance,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import {
  EditableFormItem,
  Select,
  SubmitButtons,
  Text,
  requiredRules
} from 'src/components'
import { useStyles } from 'src/features/stock-administration/components/Applications/styles'
import { useDebounce, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  fields: FormListFieldData[]
  form: FormInstance
  data?: InternalInventoryDocumentResponse
  issuanceData?: InternalInventoryDocumentResponse
  isSubmitted?: boolean
  add: () => void
  remove: (index: number) => void
}

const fieldsToReset = [
  'barCode',
  'name',
  'quantity',
  'currentQuantity',
  'unit',
  'inventoryItemId',
  'expirationDate',
  'producer'
]

export const LocalAdminConsumableList = ({
  fields,
  form,
  isSubmitted,
  data,
  issuanceData,
  add,
  remove
}: Props) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [editingIndex, setEditingIndex] = useState<number>()
  const [isNewItem, setIsNewItem] = useState(false)
  const [search, setSearch] = useState('')

  const { t } = useLocales()
  const palette = useTheme()
  const debouncedSearch = useDebounce(search)
  const { styles } = useStyles()

  const { userData } = useUser()

  const pagination = {
    current: currentPage,
    pageSize: DEFAULT_PER_PAGE,
    total: data?.items?.length,
    onChange: (page: number) => {
      setCurrentPage(page)
    },
    hideOnSinglePage: true
  }

  const {
    data: medicinesData,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage,
    hasNextPage: hasNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [useStocksServiceGetInventoryListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getInventoryListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        undefined,
        undefined,
        undefined,
        [String(userData?.currentLegalEntityId)],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        IssuanceAllowance.ALLOWED
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: InventoryListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const selectedItems = form?.getFieldValue('inquiryItems') || []

  const selectedIds = selectedItems.map(
    (item: { inventoryItemId: string }) => item?.inventoryItemId
  )

  const medicine = medicinesData?.pages.flatMap((page) => page.data) || []

  const options = medicine
    .map((item) => ({
      value: item?.id,
      label: `${item?.product?.name} ${item?.doses} ( ${t('stock_applications.series')}: ${item?.barCode} - ${t('common.expiration_date')}: ${item?.expirationDate} )`,
      item
    }))
    .filter((item) => !selectedIds?.includes(item?.value))

  const addConsumable = async () => {
    const medicine = form.getFieldValue('medicine')

    if (medicine?.length > 0) {
      const fieldsToValidate = medicine
        .map((_: { name?: string; quantity?: number }, index: number) => [
          ['medicine', index, 'name'],
          ['medicine', index, 'quantity']
        ])
        .flat()

      await form.validateFields(fieldsToValidate)
    }

    add()
    setEditingIndex(fields.length)
    setIsNewItem(true)
  }

  const onSave = async () => {
    const medicine = form.getFieldValue('medicine')

    const fieldsToValidate = medicine
      .map((_: { name?: string; quantity?: number }, index: number) => [
        ['medicine', index, 'barCode'],
        ['medicine', index, 'name'],
        ['medicine', index, 'quantity'],
        ['medicine', index, 'currentQuantity'],
        ['medicine', index, 'unit'],
        ['medicine', index, 'inventoryItemId'],
        ['medicine', index, 'producer'],
        ['medicine', index, 'expirationDate']
      ])
      .flat()

    const values = await form.validateFields(fieldsToValidate)

    if (values) {
      setIsNewItem(false)
      setEditingIndex(undefined)
      form.setFieldsValue({ medicine: values?.medicine })
    }
  }

  const onCancel = (index: number) => {
    if (isNewItem) {
      remove(index)
    } else {
      form.resetFields(fieldsToReset.map((field) => ['medicine', index, field]))
    }

    setIsNewItem(false)
    setEditingIndex(undefined)
  }

  const onSelect = (index: number, value: string) => {
    const data = form.getFieldValue('medicine')

    const selectedItem = options?.find((item) => item?.value === value)

    if (selectedItem) {
      data[index] = {
        ...data[index],
        barCode: selectedItem?.item?.barCode,
        name: `${selectedItem?.item?.product?.name} ${selectedItem?.item?.doses}`,
        producer: selectedItem?.item?.producer?.name,
        inventoryItemId: selectedItem?.item?.id,
        currentQuantity: selectedItem?.item?.currentQuantity,
        expirationDate: selectedItem?.item?.expirationDate,
        unit: {
          name: selectedItem?.item?.productForm?.name,
          id: selectedItem?.item?.productForm?.id
        }
      }

      setSearch('')
      form.setFieldValue('medicine', data)
    }
  }

  const columns: TableColumnsType = [
    {
      key: 'barCode',
      width: '10%',
      title: t('stock_applications.series'),
      render(_: unknown, __: unknown, index: number) {
        const barCode = form.getFieldValue(['medicine', index, 'barCode'])

        return <Text>{barCode || '-'}</Text>
      }
    },
    {
      key: 'name',
      width: '20%',
      title: t('common.commercial_naming'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'name']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <Select
              allowClear
              showSearch
              popupMatchSelectWidth={false}
              filterOption={false}
              onSearch={(value) => setSearch(value)}
              onClear={() => setSearch('')}
              loading={isFetchingMedicines}
              options={options}
              onSelect={(value) => onSelect(index, value)}
              placeholder={t('common.active_component')}
              onPopupScroll={(e) => {
                const target = e.target as HTMLElement
                if (
                  target.scrollTop + target.offsetHeight ===
                  target.scrollHeight
                )
                  hasNextMedicinesPage && fetchNextMedicinesPage()
              }}
              notFoundContent={
                isFetchingMedicines ? (
                  <Flex justify="center">
                    <Spin spinning />
                  </Flex>
                ) : (
                  <Empty />
                )
              }
            />
          </EditableFormItem>
        )
      }
    },
    ...(!issuanceData
      ? [
          {
            width: '10%',
            key: 'expirationDate',
            align: 'center' as const,
            title: t('common.expiration_date'),
            render(_: unknown, __: unknown, index: number) {
              const expirationDate = form.getFieldValue([
                'medicine',
                index,
                'expirationDate'
              ])

              return (
                <Text>
                  {expirationDate
                    ? dayjs(expirationDate).format(dayMonthYearBackSlash)
                    : '-'}
                </Text>
              )
            }
          }
        ]
      : []),
    {
      key: 'currentQuantity',
      width: '20%',
      align: 'center',
      title: t('stock_write_offs.current_quantity'),
      render(_, __, index) {
        const currentQuantity = form.getFieldValue([
          'medicine',
          index,
          'currentQuantity'
        ])

        return <Text>{currentQuantity || '-'}</Text>
      }
    },
    {
      key: 'quantity',
      width: '20%',
      align: 'center',
      title: t('stock_applications.to_release'),
      render: (_, __, index) => {
        const currentQuantity = form.getFieldValue([
          'medicine',
          index,
          'currentQuantity'
        ])

        return (
          <EditableFormItem
            name={[index, 'quantity']}
            isEditing={editingIndex === index}
            rules={requiredRules?.concat({
              max: currentQuantity,
              min: 0,
              message: t('errors.quantity', {
                max: currentQuantity
              }),
              type: 'number'
            })}
          >
            <InputNumber
              min={0}
              placeholder={t('stock_applications.to_release')}
              className="full-width"
            />
          </EditableFormItem>
        )
      }
    },
    {
      width: '20%',
      key: 'unit',
      align: 'center',
      title: t('common.unit'),
      render(_, __, index) {
        const unit = form.getFieldValue(['medicine', index, 'unit', 'name'])

        return <Text color={palette.green7}>{unit || '-'}</Text>
      }
    },
    ...(!isSubmitted
      ? [
          {
            width: 105,
            key: 'actions',
            fixed: 'right' as const,
            render: (_: unknown, __: unknown, index: number) => (
              <SubmitButtons
                editingIndex={editingIndex}
                isEditing={editingIndex === index}
                onSave={onSave}
                onCancel={() => onCancel(index)}
                onEdit={() => setEditingIndex(index)}
                onDelete={() => remove(index)}
              />
            )
          }
        ]
      : [])
  ]

  return (
    <Card
      title={t('stock_applications.medicine_consumables_to_release')}
      className={classNames(styles.card, styles.medicineCard)}
    >
      <Table
        className="full-width"
        dataSource={fields}
        bordered
        pagination={pagination}
        scroll={{ x: 'max-content' }}
        columns={columns}
        footer={() =>
          !isSubmitted && (
            <Flex justify="center">
              <Button onClick={addConsumable} icon={<PlusOutlined />}>
                {t('analysis.add_test.add_consumable')}
              </Button>
            </Flex>
          )
        }
      />
    </Card>
  )
}
